import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Loading, Modal, Typography } from "@/components/atoms";
// Import directly (not via the @/hooks barrel): the barrel transitively pulls
// an .xlsx asset into the module graph, which breaks vitest transforms.
import { useOperator } from "@/hooks/useOperator";
import { useGetVehicleCategoryHierarchyQuery } from "@/redux/apis/vehicleGuest/vehicleGuestApi";
import { clsx } from "@/utils";
// Same category silhouettes as the vehicle-type mapping step (Step 2 picker).
import { getCategoryIllustration } from "../../VehicleTypes/AddVehicleType/categoryIllustrations";

interface HowOffloadingWorksModalProps {
  open: boolean;
  onClose: () => void;
}

const Term = ({ children }: { children: string }) => <span className="font-medium text-primary">{children}</span>;

const STEPS = [
  {
    icon: "Mobile",
    title: "Client selects a vehicle type",
    body: (
      <>
        When making a booking, the client picks a vehicle type, for example <Term>Custom Sedan</Term>. This is your internal
        label, defined in your Vehicle Type settings.
      </>
    ),
  },
  {
    icon: "Car",
    title: "System tries to dispatch internally",
    body: (
      <>
        The system looks for available drivers in your fleet assigned to the <Term>Custom Sedan</Term> vehicle type and offers
        them the booking.
      </>
    ),
  },
  {
    icon: "CloseCircle",
    title: "No driver accepts — job is offloaded",
    body: (
      <>
        If no internal driver accepts, the booking is offloaded to the external network. The system now uses your Vehicle Type
        mapping to describe the vehicle to the network.
      </>
    ),
  },
  {
    icon: "Global",
    title: "Network searches by vehicle category",
    body: (
      <>
        Because <Term>Custom Sedan</Term> is mapped to <Term>Executive Sedan</Term> in your settings, the network searches for
        drivers whose vehicle is categorised as <Term>Executive Sedan</Term>.
      </>
    ),
    hasHierarchyLink: true,
  },
  {
    icon: "TickCircle",
    title: "Matching drivers see the booking",
    body: (
      <>
        Only drivers whose vehicle category matches <Term>Executive Sedan</Term> will see and can accept the booking. If a
        vehicle&apos;s category doesn&apos;t match, its driver won&apos;t see it at all.
      </>
    ),
  },
] as const;

const CategoryIllustration = ({ uuid, name }: { uuid: string; name: string }) => {
  const Illustration = getCategoryIllustration({ uuid, name });
  return <Illustration className="h-6 w-10 shrink-0 text-neutral-black" aria-hidden />;
};

// Row/column order per the Vehicle Dispatch Hierarchy document — Sport sits
// between the sedans and the SUVs (the API returns DB-id order, and the
// Step-2 category picker deliberately lists Sports Car last, so this order is
// specific to the hierarchy view). Unknown categories are appended.
const HIERARCHY_DISPLAY_ORDER = [
  "vca-economy-sedan",
  "vca-executive-sedan",
  "vca-premium-sedan",
  "vca-sports-car",
  "vca-suv-economy",
  "vca-suv-executive",
  "vca-7-seater-people-mover",
  "vca-11-seater-people-mover",
  "vca-bus",
];

const displayRank = (uuid: string): number => {
  const rank = HIERARCHY_DISPLAY_ORDER.indexOf(uuid);
  return rank === -1 ? HIERARCHY_DISPLAY_ORDER.length : rank;
};

const HierarchyGrid = () => {
  const { data, isFetching, isError } = useGetVehicleCategoryHierarchyQuery();

  if (isFetching) return <Loading />;
  if (isError || !data?.length) {
    return <Typography className="text-neutral-dark-gray">The vehicle category hierarchy could not be loaded.</Typography>;
  }

  const categories = [...data].sort((a, b) => displayRank(a.uuid) - displayRank(b.uuid));

  return (
    <div className="max-h-[420px] overflow-auto rounded border border-neutral-gray">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-20 min-w-[150px] bg-neutral-surface-gray p-3 text-left">
              <Typography variant="small">Vehicle Category</Typography>
            </th>
            {categories.map((column) => (
              <th key={column.uuid} className="sticky top-0 z-10 min-w-[110px] bg-neutral-surface-gray p-3 text-center">
                <Typography variant="small">{column.name} Booking</Typography>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((row) => (
            <tr key={row.uuid} className="border-t border-neutral-gray">
              <td className="sticky left-0 z-10 bg-white p-3">
                <div className="flex items-center gap-2">
                  <CategoryIllustration uuid={row.uuid} name={row.name} />
                  <Typography variant="small-regular">{row.name}</Typography>
                </div>
              </td>
              {categories.map((column) => {
                const serves = row.serves.some((served) => served.uuid === column.uuid);
                return (
                  <td
                    key={column.uuid}
                    className={clsx("border-l border-neutral-gray p-3 text-center", serves && "bg-success-light")}
                  >
                    {serves && <Icon name="TickCircle" variant="Bold" size="md" className="inline-block text-success" />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const HowOffloadingWorksModal = ({ open, onClose }: HowOffloadingWorksModalProps) => {
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();
  const [view, setView] = useState<"steps" | "hierarchy">("steps");

  // Always reopen on the steps view.
  useEffect(() => {
    if (open) setView("steps");
  }, [open]);

  const handleManageTypes = () => {
    onClose();
    navigate(`/operator/${operatorId}/config/trip/vehicle-types`);
  };

  const isHierarchy = view === "hierarchy";

  return (
    <Modal
      open={open}
      onClose={onClose}
      onBack={isHierarchy ? () => setView("steps") : undefined}
      title={isHierarchy ? "Vehicle Category Hierarchy" : "How offloading works"}
      description={
        isHierarchy
          ? "Each vehicle category can cover its own booking type, plus bookings for categories below it. A green check shows which booking types a category can serve on the external network."
          : "When a booking can't be filled internally, it's offloaded to the external network. This only applies if you have automated offloading enabled or manually offload a job."
      }
      variant={isHierarchy ? "web-large" : "web-medium"}
      renderButtons={() => (
        <>
          {isHierarchy ? (
            <Button variant="tertiary" size="md" onClick={() => setView("steps")}>
              Back
            </Button>
          ) : (
            <Button variant="tertiary" size="md" onClick={handleManageTypes}>
              Manage Vehicle Types
            </Button>
          )}
          <Button variant="primary" size="md" onClick={onClose}>
            Close
          </Button>
        </>
      )}
    >
      {isHierarchy ? (
        <HierarchyGrid />
      ) : (
        <div className="flex flex-col">
          {STEPS.map((step, index) => (
            <div key={step.title} className="flex gap-4">
              <div className="flex flex-col items-center">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light">
                  <Icon name={step.icon} size="sm" className="text-primary" />
                </span>
                {index < STEPS.length - 1 && <span aria-hidden className="w-px flex-1 bg-neutral-mid-gray" />}
              </div>
              <div className="flex flex-col gap-1 pb-6">
                <Typography variant="action">{step.title}</Typography>
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  {step.body}
                </Typography>
                {"hasHierarchyLink" in step && step.hasHierarchyLink && (
                  <button
                    type="button"
                    className="self-start text-left text-info underline"
                    onClick={() => setView("hierarchy")}
                  >
                    See the full Vehicle Category Hierarchy
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
